import { Helmet } from 'react-helmet-async';
import {
  SITE_NAME,
  SITE_URL,
  SITE_DESCRIPTION,
  SITE_TAGLINE,
  SOCIAL_IMAGE,
  TWITTER_HANDLE,
} from '../../constants/site';

/**
 * One place for every page's meta tags, so prerendered HTML always ships a
 * title, description, canonical URL, Open Graph / Twitter tags and JSON-LD.
 *
 * @param {string}   title          page title (site name is appended)
 * @param {string}   description    meta description (~155 chars is the useful max)
 * @param {string}   path           canonical path, e.g. "/blog/programming/foo--123"
 * @param {string}   image          cover image URL — relative paths are made absolute
 * @param {object|object[]} jsonLd  schema.org object(s); emitted as separate scripts
 * @param {boolean}  noIndex        set for admin/utility/404 pages
 * @param {string}   type           og:type — "website" | "article"
 * @param {string}   publishedTime  ISO date, articles only
 * @param {string}   modifiedTime   ISO date, articles only
 * @param {string}   author         articles only
 * @param {string}   section        articles only — the category name
 * @param {string[]} tags           articles only
 */
function Seo({
  title,
  description,
  path = '/',
  image,
  jsonLd,
  noIndex = false,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  section,
  tags = [],
}) {
  // The tagline, not the description: Google truncates titles at roughly 60
  // characters, and the description is far longer than that.
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — ${SITE_TAGLINE}`;
  const desc = description || SITE_DESCRIPTION;

  // Canonical must be absolute and must not carry query strings — ?q= and
  // ?category= are UI state, not distinct pages, so they all consolidate here.
  const canonical = `${SITE_URL}${path.split('?')[0]}`;

  // Social scrapers do not resolve relative image paths.
  const absoluteImage = image
    ? image.startsWith('http')
      ? image
      : `${SITE_URL}${image}`
    : `${SITE_URL}${SOCIAL_IMAGE}`;

  const schemas = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'}
      />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="675" />
      <meta property="og:image:alt" content={title || SITE_NAME} />

      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && <meta property="article:author" content={author} />}
      {type === 'article' && section && <meta property="article:section" content={section} />}
      {type === 'article' &&
        tags.map((tag) => <meta key={tag} property="article:tag" content={tag} />)}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={absoluteImage} />
      {TWITTER_HANDLE && <meta name="twitter:site" content={TWITTER_HANDLE} />}

      {schemas.map((schema, i) => (
        // eslint-disable-next-line react/no-danger
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default Seo;
